// Тосты (components/Toast/README.md; ux.md, раздел 2): короткий итог действия внизу справа,
// стопкой, новые снизу. Текст — прошедшее время и объект, затем одно действие: «Отменить»,
// «Открыть», «Повторить». Живёт 4 с, короткое подтверждение — 2 с, важное пояснение — 6 с;
// при наведении и фокусе время останавливается.
//
// Озвучивание (FR-041б): две постоянные живые области — role="status" (polite) для итогов и
// role="alert" (assertive) для ошибок. Свои тосты, а не Radix Toast: тост Radix — слой
// DismissableLayer и перехватывает Esc у открытого окна, а Esc закрывает верхний слой из
// списка ux.md (меню → окно → боковая панель), где тостов нет.
//
// Поверх окон: окно Radix прячет от диктора всё вокруг себя (hideOthers), развёрнутая панель —
// aria-modal. Пока открыт слой со своей живой областью (ToastLayerRegion: окна, боковая панель,
// палитра), новый тост озвучивается в ней; тост, показанный вместе с закрытием слоя, — в области
// ниже. Сами тосты помечены aria-live="off": hideOthers не прячет их от диктора, «Отменить» в тосте
// над окном доступна и ему.
//
// Уход (044, MOT-22 – MOT-24, research Р7): тост уходит при любом уходе — по времени, крестиком,
// своим действием, вытеснением — кадром t-toast-out «Табеля» (160 мс). Уходящий тост выходит из
// потока и гаснет на своём месте, не принимает нажатий (inert) и не озвучивается; соседи
// занимают место плавно — приём FLIP (element.animate, --motion-exit, --ease-out; время токена —
// в мс или в с, как его пишет сборка). При «Уменьшить движение» уходящий держит место и гаснет
// за 120 мс, соседи встают после без анимации.
import { CircleAlert, CircleCheck, Info, X } from 'lucide-react';
import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../lib/cx.ts';
import { Icon } from '../lib/icon.tsx';
import { usePresence } from '../lib/presence.ts';
import { ToastContext, ToastLayersContext } from '../lib/toast-context.ts';
import type {
  ToastAction,
  ToastApi,
  ToastDuration,
  ToastKind,
  ToastLayers,
  ToastLiveElements,
  ToastOptions,
} from '../lib/toast-context.ts';
import { IconButton } from './IconButton.tsx';
import { OperationCode, operationSpokenText } from './OperationCode.tsx';

export { useToast } from '../lib/toast-context.ts';
export type {
  ToastAction,
  ToastApi,
  ToastDuration,
  ToastKind,
  ToastOptions,
} from '../lib/toast-context.ts';

export const toastDurations: Record<ToastDuration, number> = {
  short: 2000,
  normal: 4000,
  long: 6000,
};

interface ToastItem {
  id: number;
  text: string;
  kind: ToastKind;
  action?: ToastAction;
  code?: string;
  operation?: string;
  /** Где озвучен: номер слоя (LayerHost) или 0 — постоянные области провайдера. */
  layer: number;
  /** Тост уходит: на экране до конца выхода, не озвучивается и не нажимается (044). */
  leaving?: boolean;
}

/** Место тоста до ухода: верх — для FLIP соседей, низ от края списка и ширина — для ухода. */
interface ToastPlace {
  top: number;
  bottom: number;
  width: number;
}

/** Анимация FLIP соседа: по ней прежняя отменяется, если сосед сдвигается снова. */
const FLIP_ID = 'kp-toast-flip';

function reducedMotion(): boolean {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

/** Места тостов на экране сейчас, по номеру. */
function measure(list: HTMLElement | null): Map<number, ToastPlace> {
  const places = new Map<number, ToastPlace>();
  if (!list) return places;
  const bottom = list.getBoundingClientRect().bottom;
  for (const row of list.querySelectorAll<HTMLElement>(':scope > li[data-toast]')) {
    const rect = row.getBoundingClientRect();
    places.set(Number(row.dataset.toast), {
      top: rect.top,
      bottom: bottom - rect.bottom,
      width: rect.width,
    });
  }
  return places;
}

/**
 * Время токена в миллисекундах: «160ms» или «.16s» — сборка сжимает стили и пишет секунды
 * (044, T041: parseFloat(«.16s») давал 0,16 мс, и соседи прыгали). Не время — 0.
 */
function cssDurationMs(value: string): number {
  const match = /^(-?(?:\d+\.?\d*|\.\d+))(ms|s)$/.exec(value.trim());
  if (!match) return 0;
  const amount = Number.parseFloat(match[1]!);
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  return match[2] === 's' ? amount * 1000 : amount;
}

/** Длительность и кривая сдвига соседей — токены «Табеля». */
function flipTiming(): { duration: number; easing: string } {
  const root = getComputedStyle(document.documentElement);
  return {
    duration: cssDurationMs(root.getPropertyValue('--motion-exit')),
    easing: root.getPropertyValue('--ease-out').trim() || 'ease-out',
  };
}

/**
 * Уходящие тосты — вне потока на своём месте; соседи сдвигаются от прежнего места к новому
 * (FLIP). before — места до ухода, снятые в момент ухода.
 */
function settleRows(list: HTMLElement, before: ReadonlyMap<number, ToastPlace>): void {
  const rows = Array.from(list.querySelectorAll<HTMLElement>(':scope > li[data-toast]'));
  for (const row of rows) {
    const was = before.get(Number(row.dataset.toast));
    if (!was || !row.hasAttribute('data-leaving') || row.style.position === 'absolute') continue;
    Object.assign(row.style, {
      position: 'absolute',
      right: '0px',
      bottom: `${was.bottom}px`,
      width: `${was.width}px`,
    });
  }
  const { duration, easing } = flipTiming();
  if (duration <= 0) return;
  for (const row of rows) {
    const was = before.get(Number(row.dataset.toast));
    if (!was || row.hasAttribute('data-leaving') || typeof row.animate !== 'function') continue;
    for (const animation of row.getAnimations()) {
      if (animation.id === FLIP_ID) animation.cancel();
    }
    const shift = was.top - row.getBoundingClientRect().top;
    if (Math.abs(shift) < 0.5) continue;
    row.animate([{ transform: `translateY(${shift}px)` }, { transform: 'none' }], {
      id: FLIP_ID,
      duration,
      easing,
    });
  }
}

/** Открытый слой со своей живой областью. */
interface LayerHost extends ToastLiveElements {
  id: number;
}

interface Timer {
  handle: number | undefined;
  startedAt: number;
  remaining: number;
}

const kindIcon = {
  ok: { icon: CircleCheck, className: 'ok' },
  error: { icon: CircleAlert, className: 'bad' },
  info: { icon: Info, className: 'kp-info' },
} as const;

/** Для диктора: с номером операции — «…. Код SYS-001, операция 7F3K 92QD 1A2B». */
function withCode(item: ToastItem): string {
  if (item.code && item.operation) {
    return `${item.text}. ${operationSpokenText(item.code, item.operation)}`;
  }
  return item.code ? `${item.text}. Код ошибки ${item.code}` : item.text;
}

export interface ToastProviderProps {
  children: ReactNode;
  /**
   * Нет связи: тосты об успехе не показываются (ux.md, «Нет связи»). Функция читается в момент
   * показа тоста: связь, которая вернулась с ответом сервера, видна тосту этой же операции ещё
   * до перерисовки. Значение (boolean) тост видит только после перерисовки с ним.
   */
  offline?: boolean | (() => boolean);
  /** Сколько тостов видно одновременно; старые уходят. */
  max?: number;
}

export function ToastProvider({ children, offline = false, max = 3 }: ToastProviderProps) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timers = useRef(new Map<number, Timer>());
  const paused = useRef(false);
  const nextId = useRef(1);
  const offlineRef = useRef(offline);
  /** onDismiss тостов, которые ещё на экране. */
  const dismissals = useRef(new Map<number, () => void>());
  /** Открытые слои по порядку открытия: последний — верхний. */
  const [layers, setLayers] = useState<readonly LayerHost[]>([]);
  const layersRef = useRef<readonly LayerHost[]>([]);
  const nextLayer = useRef(1);
  /** Тосты, которые ещё ни разу не попали на страницу: их слой мог закрыться вместе с ними. */
  const fresh = useRef(new Set<number>());
  const listRef = useRef<HTMLOListElement>(null);
  /** Места тостов в момент ухода: до перерисовки, в которой тост уходит. */
  const before = useRef<Map<number, ToastPlace> | null>(null);
  /** Снять места тостов перед уходом — один раз до ближайшей перерисовки. */
  const remember = useCallback(() => {
    before.current ??= measure(listRef.current);
  }, []);

  // До эффектов отрисовки: тост, показанный сразу после перерисовки, видит новое значение.
  useLayoutEffect(() => {
    offlineRef.current = offline;
  }, [offline]);

  /** Тост ушёл без своего действия: его onDismiss — один раз. */
  const settle = useCallback((id: number) => {
    const onDismiss = dismissals.current.get(id);
    dismissals.current.delete(id);
    onDismiss?.();
  }, []);

  const dismiss = useCallback(
    (id: number) => {
      // Вне обновления состояния: onDismiss может отправить запрос.
      queueMicrotask(() => settle(id));
      const timer = timers.current.get(id);
      if (timer?.handle !== undefined) window.clearTimeout(timer.handle);
      timers.current.delete(id);
      // Последний тост закрыт под курсором — следующие снова идут по времени.
      if (timers.current.size === 0) paused.current = false;
      remember();
      setItems((list) =>
        list.map((item) => (item.id === id && !item.leaving ? { ...item, leaving: true } : item)),
      );
    },
    [settle, remember],
  );

  /** Выход тоста кончился — его больше нет на странице. */
  const drop = useCallback((id: number) => {
    setItems((list) => list.filter((item) => item.id !== id));
  }, []);

  const start = useCallback(
    (id: number, timer: Timer) => {
      timer.startedAt = Date.now();
      timer.handle = window.setTimeout(() => dismiss(id), timer.remaining);
    },
    [dismiss],
  );

  const show = useCallback(
    (text: string, options: ToastOptions = {}) => {
      const kind = options.kind ?? 'ok';
      const current = offlineRef.current;
      const offlineNow = typeof current === 'function' ? current() : current;
      if (offlineNow && kind !== 'error') return 0;
      const id = nextId.current++;
      const duration =
        typeof options.duration === 'number'
          ? options.duration
          : toastDurations[options.duration ?? 'normal'];
      const item: ToastItem = {
        id,
        text,
        kind,
        action: options.action,
        code: options.code,
        operation: options.operation,
        layer: layersRef.current.at(-1)?.id ?? 0,
      };
      fresh.current.add(id);
      remember();
      setItems((list) => {
        const next = [...list, item];
        const live = next.filter((entry) => !entry.leaving);
        // Лишние старые тосты уходят вместе со своими таймерами — с выходом, как все (MOT-22).
        const cut = new Set(live.slice(0, Math.max(0, live.length - max)).map((old) => old.id));
        for (const old of cut) {
          const timer = timers.current.get(old);
          if (timer?.handle !== undefined) window.clearTimeout(timer.handle);
          timers.current.delete(old);
          queueMicrotask(() => settle(old));
        }
        return cut.size === 0
          ? next
          : next.map((entry) => (cut.has(entry.id) ? { ...entry, leaving: true } : entry));
      });
      if (options.onDismiss) dismissals.current.set(id, options.onDismiss);
      const timer: Timer = { handle: undefined, startedAt: 0, remaining: duration };
      timers.current.set(id, timer);
      if (!paused.current) start(id, timer);
      return id;
    },
    [max, start, settle, remember],
  );

  const pause = useCallback(() => {
    if (paused.current) return;
    paused.current = true;
    const now = Date.now();
    for (const timer of timers.current.values()) {
      if (timer.handle === undefined) continue;
      window.clearTimeout(timer.handle);
      timer.handle = undefined;
      timer.remaining = Math.max(0, timer.remaining - (now - timer.startedAt));
    }
  }, []);

  const resume = useCallback(() => {
    if (!paused.current) return;
    paused.current = false;
    for (const [id, timer] of timers.current) {
      if (timer.handle === undefined) start(id, timer);
    }
  }, [start]);

  useEffect(() => {
    const all = timers.current;
    return () => {
      for (const timer of all.values()) {
        if (timer.handle !== undefined) window.clearTimeout(timer.handle);
      }
      all.clear();
    };
  }, []);

  useLayoutEffect(() => {
    // Отрисовка дошла до страницы: эти тосты уже в своих живых областях.
    for (const item of items) fresh.current.delete(item.id);
    // Тосты ушли: уходящие — вне потока на своём месте, соседи — плавно на новое место.
    const was = before.current;
    before.current = null;
    if (was && listRef.current && !reducedMotion()) settleRows(listRef.current, was);
  }, [items]);

  const layerApi = useMemo<ToastLayers>(
    () => ({
      register: (elements) => {
        const host: LayerHost = { id: nextLayer.current++, ...elements };
        layersRef.current = [...layersRef.current, host];
        setLayers(layersRef.current);
        return () => {
          layersRef.current = layersRef.current.filter((layer) => layer !== host);
          setLayers(layersRef.current);
          // Тост показан вместе с закрытием слоя («Роль передана» и закрыть окно): закрытый слой
          // его уже не озвучит — озвучиваем в области ниже. Уже озвученные не повторяются.
          const below = layersRef.current.at(-1)?.id ?? 0;
          const unheard = new Set(fresh.current);
          if (unheard.size === 0) return;
          setItems((list) =>
            list.map((item) =>
              item.layer === host.id && unheard.has(item.id) ? { ...item, layer: below } : item,
            ),
          );
        };
      },
    }),
    [],
  );

  const api = useMemo<ToastApi>(
    () => ({
      show,
      ok: (text, options) => show(text, { ...options, kind: 'ok' }),
      error: (text, options) => show(text, { ...options, kind: 'error' }),
      dismiss,
    }),
    [show, dismiss],
  );

  /** Текст для диктора: тосты этого слоя (0 — без слоя) нужного вида; уходящие — нет (MOT-24). */
  const announce = (kinds: readonly ToastKind[], layer: number) =>
    items
      .filter((item) => !item.leaving && item.layer === layer && kinds.includes(item.kind))
      .map((item) => (
        <p key={item.id}>
          {withCode(item)}
          {item.action ? ` · ${item.action.label}` : ''}
        </p>
      ));

  return (
    <ToastContext.Provider value={api}>
      <ToastLayersContext.Provider value={layerApi}>{children}</ToastLayersContext.Provider>
      <div className="kp-sr-only" role="status" aria-live="polite">
        {announce(['ok', 'info'], 0)}
      </div>
      <div className="kp-sr-only" role="alert" aria-live="assertive">
        {announce(['error'], 0)}
      </div>
      {layers.map((layer) => (
        <Fragment key={layer.id}>
          {createPortal(announce(['ok', 'info'], layer.id), layer.polite)}
          {createPortal(announce(['error'], layer.id), layer.assertive)}
        </Fragment>
      ))}
      {items.length > 0 ? (
        <section
          className="kp-toasts"
          aria-label="Сообщения"
          // Озвучивают живые области выше; aria-live="off" — чтобы окно Radix (hideOthers) не
          // прятало тосты от диктора: области с aria-live он оставляет.
          aria-live="off"
          onPointerEnter={pause}
          onPointerLeave={resume}
          onFocus={pause}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) resume();
          }}
        >
          <ol ref={listRef} className="kp-toast-list">
            {items.map((item) => (
              <ToastRow
                key={item.id}
                item={item}
                onDismiss={dismiss}
                onGone={drop}
                onAction={(action) => {
                  // Действие выбрано — onDismiss не вызывается.
                  dismissals.current.delete(item.id);
                  dismiss(item.id);
                  action.onClick();
                }}
              />
            ))}
          </ol>
        </section>
      ) : null}
    </ToastContext.Provider>
  );
}

interface ToastRowProps {
  item: ToastItem;
  onDismiss: (id: number) => void;
  onAction: (action: ToastAction) => void;
  /** Выход кончился: тост можно убрать из списка. */
  onGone: (id: number) => void;
}

/** Тост в стопке: живёт до конца выхода (usePresence), уходящий не нажимается. */
function ToastRow({ item, onDismiss, onAction, onGone }: ToastRowProps) {
  const alive = !item.leaving;
  const { mounted, state, ref } = usePresence(alive);
  useEffect(() => {
    if (!mounted) onGone(item.id);
  }, [mounted, onGone, item.id]);
  if (!mounted) return null;
  const look = kindIcon[item.kind];
  return (
    <li data-toast={item.id} data-leaving={alive ? undefined : ''} inert={!alive}>
      <div
        ref={ref}
        className={cx('t-toast', !alive && 'is-leaving')}
        data-kind={item.kind}
        data-state={state}
      >
        <span className={look.className} aria-hidden="true">
          <Icon icon={look.icon} />
        </span>
        <span className="kp-toast-text">
          {item.text}
          {item.code && item.operation ? (
            // Строкой ниже: «Код … · операция …» и «Скопировать номер» (FR-028).
            <OperationCode
              code={item.code}
              operation={item.operation}
              tooltipSide="top"
              className="kp-opline"
            />
          ) : item.code ? (
            <>
              . Код ошибки <span className="kp-nowrap">{item.code}</span>
            </>
          ) : null}
        </span>
        {item.action ? (
          <button
            type="button"
            onClick={() => {
              // Уходящий тост не срабатывает: «Отменить» — не больше одного раза (MOT-24).
              if (alive && item.action) onAction(item.action);
            }}
          >
            {item.action.label}
          </button>
        ) : null}
        {/* Крестик — кнопка-иконка с подписью и подсказкой (README «Иконки», FR-041а). */}
        <IconButton
          icon={X}
          label="Закрыть"
          size="sm"
          tooltipSide="top"
          className="x"
          onClick={() => {
            if (alive) onDismiss(item.id);
          }}
        />
      </div>
    </li>
  );
}
